<template>
  <div
    class="avatar-container relative box-content flex aspect-square shrink-0 items-center justify-center overflow-hidden text-center leading-[1.1] select-none"
    :style="{
      width: `${sizeInPx}px`,
      height: `${sizeInPx}px`,
    }"
    :class="{
      'rounded-full!': shape === 'circle',
      'rounded-md text-[11px]': size === ComponentSize.sm,
      'rounded-lg text-[16px]': size === ComponentSize.md,
      'rounded-[10px] text-[20px]': size === ComponentSize.lg,
      'rounded-xl text-[40px]': size === ComponentSize.xl,
      'bg-avatar-bgr-orange text-avatar-fgr-orange': color === ComponentColor.Orange && !image,
      'bg-avatar-bgr-pink text-avatar-fgr-pink': color === ComponentColor.Pink && !image,
      'bg-avatar-bgr-brown text-avatar-fgr-brown': color === ComponentColor.Brown && !image,
      'bg-avatar-bgr-purple text-avatar-fgr-purple': color === ComponentColor.Purple && !image,
      'bg-avatar-bgr-teal text-avatar-fgr-teal': color === ComponentColor.Teal && !image,
      'bg-avatar-bgr-red text-avatar-fgr-red': color === ComponentColor.Red && !image,
      'bg-avatar-bgr-yellow text-avatar-fgr-yellow': color === ComponentColor.Yellow && !image,
      'bg-avatar-bgr-blue text-avatar-fgr-blue': color === ComponentColor.Blue && !image,
      'bg-avatar-bgr-green text-avatar-fgr-green': color === ComponentColor.Green && !image,

      'bg-bgr-accent-primary text-fgr-white': !color && !image,
      selected,
    }"
  >
    <slot>
      <img
        v-if="image"
        class="h-auto w-full text-center"
        :src="image"
        :alt="entity?.name || ''"
        :height="sizeInPx"
        :width="sizeInPx"
        @error="onError"
      />
      <Icon v-else-if="icon" :src="icon" :size="iconSize" />
      <div v-else class="font-semibold">
        {{ letterName }}
      </div>
    </slot>
    <slot name="overlay" />
  </div>
</template>

<script setup lang="ts">
  export interface AvatarProps {
    color?: ComponentColor;
    entity?: {
      name?: string;
      email?: string;
      image?: string | null | File;
    };
    index?: number;
    randomColor?: boolean;
    size?: ComponentSize.sm | ComponentSize.md | ComponentSize.lg | ComponentSize.xl;
    type?: AvatarType;
    useLetters?: boolean;
    shape?: "square" | "circle";
    selected?: boolean;
    icon?: string;
  }

  const props = withDefaults(defineProps<AvatarProps>(), {
    color: ComponentColor.Purple,
    entity: undefined,
    index: undefined,
    size: ComponentSize.md,
    type: AvatarType.User,
    useLetters: true,
    shape: "square",
    selected: false,
    icon: undefined,
  });

  const image = ref<string | null>();

  defineOptions({
    name: "Avatar",
  });

  const sizeInPx = computed(() => {
    switch (props.size) {
      case "sm":
        return 20;
      case "md":
        return 28;
      case "lg":
        return 36;
      case "xl":
        return 64;
      default:
        return 28;
    }
  });

  const iconSize = computed(() => {
    switch (props.size) {
      case ComponentSize.sm:
        return ComponentSize.sm;
      case ComponentSize.lg:
        return ComponentSize.lg;
      case ComponentSize.xl:
        return ComponentSize.xl4;
      default:
        return ComponentSize.md;
    }
  });

  watchEffect(async () => {
    const img = props.entity?.image || null;

    if (img instanceof File) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (typeof result === "string") {
          image.value = result;
        }
      };
      reader.readAsDataURL(img);
    } else if (img?.startsWith("http") || img?.startsWith("api")) {
      image.value = await new Promise<string | null>((resolve) => {
        const tester = new Image();
        tester.onload = () => {
          resolve(img);
        };
        tester.onerror = () => {
          resolve(null);
        };
        tester.src = img;
      });
    } else {
      image.value = img;
      if (image.value) {
        const separator = image.value.includes("?") ? "&" : "?";
        image.value += `${separator}s=${sizeInPx.value * 2}`;
      }
    }
  });

  const color = computed(() => {
    if (props.type === AvatarType.Webshop) return "";

    //if randomColor is true, return random color if index is not provided, else return color based on index, if index is too high, start over from 0
    if (props.randomColor) {
      const colors = Object.values(ComponentColor).filter((c) => c !== ComponentColor.Grey);
      if (props.index !== undefined) return colors[props.index % colors.length];

      //hash the name to get a number
      let hash = props.entity?.name
        ? props.entity.name.split("").reduce((a: number, b: string) => a + b.charCodeAt(0), 0)
        : 0;

      // reduce the hash to a number between 0 and colors.length - 1

      while (hash >= colors.length) {
        hash = hash % colors.length;
      }

      return colors[hash];
    }

    return props.color;
  });

  const letterName = computed(() => {
    if (!props.entity?.name && !props.entity?.email) return "";
    const name = props.entity.name || props.entity.email;
    //split name on space or - and get first letter of the first word
    const nameArray = name!.split(/[\s-]/);
    const firstLetter = nameArray[0].charAt(0);

    return firstLetter.toUpperCase();
  });

  const onError = () => {
    image.value = null;
  };
</script>

<style scoped>
  .selected::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    border-radius: inherit;
    box-shadow: var(--shadow-focus);
  }
</style>
